import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import Select from 'react-select';

import { PageTitle } from '../PageTitle';
import { DashboardService, EnterprisesService, PinnedEnterprisesService } from '../../services';
import EnterpriseCard, { SORT_OPTIONS, TIMEFRAME_OPTIONS } from './enterprise_card';
import EnterpriseDetail from './enterprise_detail';

// ---------------------------------------------------------------------------
// Client-side sort for top section (API-1a has no sort param)
// ---------------------------------------------------------------------------

function sortCards(cards, sortValue) {
    return [...cards].sort((a, b) => {
        switch (sortValue) {
            case 'devicesOffline':
                return (b.gatewaysOffline + b.sensorsOffline) - (a.gatewaysOffline + a.sensorsOffline);
            case 'criticalAlertEvents':
                return b.locationsWithCriticalEvents - a.locationsWithCriticalEvents;
            case 'percentOffline': {
                const pA = a.gatewaysTotal > 0 ? a.gatewaysOffline / a.gatewaysTotal : 0;
                const pB = b.gatewaysTotal > 0 ? b.gatewaysOffline / b.gatewaysTotal : 0;
                return pB - pA;
            }
            default:
                return a.enterpriseName.localeCompare(b.enterpriseName);
        }
    });
}

// ---------------------------------------------------------------------------
// Section icons (SVG)
// ---------------------------------------------------------------------------

const OkIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_223_3907)">
            <path d="M10.8787 20.9018C10.7422 20.9018 10.6045 20.8878 10.468 20.861C9.91735 20.7513 9.44251 20.4352 9.13101 19.9697L6.28435 15.9307C6.00668 15.5363 6.10118 14.9892 6.49551 14.7115C6.64368 14.6065 6.81751 14.5517 6.99951 14.5517C7.28418 14.5517 7.55135 14.6905 7.71585 14.9227L10.5742 18.9792C10.6372 19.0737 10.7142 19.1262 10.8087 19.1448C10.832 19.1495 10.8553 19.1518 10.8787 19.1518C10.9463 19.1518 11.0128 19.1308 11.07 19.0923C11.1027 19.0702 11.1307 19.0433 11.154 19.013L20.3147 7.42215C20.4803 7.21215 20.73 7.09082 20.9995 7.09082C21.1955 7.09082 21.388 7.15732 21.5408 7.27865C21.724 7.42332 21.8407 7.63099 21.8675 7.86315C21.8943 8.09532 21.8302 8.32399 21.6855 8.50715L12.5353 20.084C12.4035 20.2578 12.2355 20.4153 12.0418 20.546C11.6953 20.7793 11.2928 20.9018 10.8787 20.9018Z" fill="#19B08D"/>
            <path d="M14 27.999C6.28017 27.999 0 21.7189 0 13.999C0 6.27919 6.28017 -0.000976562 14 -0.000976562C21.7198 -0.000976562 28 6.27919 28 13.999C28 21.7189 21.7198 27.999 14 27.999ZM14 1.74902C7.245 1.74902 1.75 7.24402 1.75 13.999C1.75 20.754 7.245 26.249 14 26.249C20.755 26.249 26.25 20.754 26.25 13.999C26.25 7.24402 20.755 1.74902 14 1.74902Z" fill="#19B08D"/>
        </g>
        <defs>
            <clipPath id="clip0_223_3907">
                <rect width="28" height="28" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

const WarningIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_223_4674)">
            <path fillRule="evenodd" clipRule="evenodd" d="M14 18.875C13.3096 18.875 12.75 19.4346 12.75 20.125C12.75 20.8154 13.3096 21.375 14 21.375C14.6904 21.375 15.25 20.8154 15.25 20.125C15.25 19.4346 14.6904 18.875 14 18.875Z" fill="#D71F34"/>
            <path d="M14 15.375V6.625" stroke="#D71F34" strokeWidth="1.5" strokeLinecap="round"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M1.49333 15.4946C0.668971 14.6693 0.668971 13.3321 1.49333 12.5068L12.5067 1.49462C12.9024 1.09794 13.4397 0.875 14 0.875C14.5603 0.875 15.0976 1.09794 15.4933 1.49462L26.5067 12.5068C27.331 13.3321 27.331 14.6693 26.5067 15.4946L15.4933 26.5068C14.6684 27.331 13.3316 27.331 12.5067 26.5068L1.49333 15.4946Z" stroke="#D71F34" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <defs>
            <clipPath id="clip0_223_4674">
                <rect width="28" height="28" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

// ---------------------------------------------------------------------------
// Section header with dropdowns
// ---------------------------------------------------------------------------

const sectionSelectBase = {
    indicatorSeparator: base => ({ ...base, display: 'block' }),
};
const timeframeSelectStyles = { ...sectionSelectBase, control: base => ({ ...base, minWidth: 160, cursor: 'pointer' }) };
const sortSelectStyles      = { ...sectionSelectBase, control: base => ({ ...base, minWidth: 200, cursor: 'pointer' }) };

const SectionControls = ({ timeframe, onTimeframeChange, sort, onSortChange }) => (
    <div className="enterprise-dashboard__section-controls">
        <span className="enterprise-dashboard__control-label">Timeframe</span>
        <Select
            classNamePrefix="react-select"
            options={TIMEFRAME_OPTIONS}
            value={TIMEFRAME_OPTIONS.find(o => o.value === timeframe)}
            onChange={o => onTimeframeChange(o.value)}
            styles={timeframeSelectStyles}
            isSearchable={false}
        />
        <span className="enterprise-dashboard__control-label">Sort By</span>
        <Select
            classNamePrefix="react-select"
            options={SORT_OPTIONS}
            value={SORT_OPTIONS.find(o => o.value === sort)}
            onChange={o => onSortChange(o.value)}
            styles={sortSelectStyles}
            isSearchable={false}
        />
    </div>
);

// ---------------------------------------------------------------------------
// Card grid
// ---------------------------------------------------------------------------

const CardGrid = ({ cards, timeframe, onPinToggle, onCardClick, protocolCache, onProtocolHover }) => (
    <div className="enterprise-dashboard__card-grid">
        {cards.map(card => (
            <div key={card.enterpriseId} className="enterprise-dashboard__card-col">
                <EnterpriseCard
                    card={card}
                    timeframe={timeframe}
                    isPinned={!!card.userPinned}
                    onPinToggle={onPinToggle}
                    onCardClick={onCardClick}
                    protocolBreakdown={protocolCache[card.enterpriseId]}
                    onProtocolHover={onProtocolHover}
                />
            </div>
        ))}
    </div>
);

// ---------------------------------------------------------------------------
// Status tab — fetches top/bottom sections independently
// ---------------------------------------------------------------------------

const EnterpriseDashboardStatus = ({ onCardClick, refreshKey }) => {
    // Top section state
    const [topCards, setTopCards]           = useState([]);
    const [topTimeframe, setTopTimeframe]   = useState(24);
    const [topSort, setTopSort]             = useState('name');
    const [topLoading, setTopLoading]       = useState(true);

    // Bottom section state
    const [bottomCards, setBottomCards]           = useState([]);
    const [bottomTimeframe, setBottomTimeframe]   = useState(24);
    const [bottomSort, setBottomSort]             = useState('name');
    const [bottomLoading, setBottomLoading]       = useState(true);
    const [bottomPage, setBottomPage]             = useState(0);
    const [bottomTotalPages, setBottomTotalPages] = useState(0);
    const [alertsDisabledOnly, setAlertsDisabledOnly] = useState(false);

    // Protocol popover cache: { [enterpriseId]: [...breakdown] }
    const [protocolCache, setProtocolCache] = useState({});
    const protocolCacheRef = useRef(protocolCache);
    useEffect(() => { protocolCacheRef.current = protocolCache; }, [protocolCache]);

    // Pin-in-flight guard
    const [pinInFlight, setPinInFlight] = useState(null);

    const sentinelRef = useRef(null);

    // --- Load top section (API-1a) ---
    const loadTopSection = useCallback((hours, silent = false) => {
        if (!silent) setTopLoading(true);
        DashboardService.getEnterpriseNeedsAttentionCards(hours).then(res => {
            setTopCards(res.data);
            setTopLoading(false);
        }).catch(err => {
            console.error('Failed to load needs-attention cards:', err);
            setTopLoading(false);
        });
    }, []);

    // --- Load bottom section (API-1b) ---
    const loadBottomSection = useCallback((hours, sort, page, silent = false) => {
        if (!silent) setBottomLoading(true);
        DashboardService.getEnterpriseAllOtherCards(hours, sort, page, 25).then(res => {
            const pageData = res.data;
            if (page === 0) {
                setBottomCards(pageData.content);
            } else {
                setBottomCards(prev => [...prev, ...pageData.content]);
            }
            setBottomTotalPages(pageData.totalPages);
            setBottomLoading(false);
        }).catch(err => {
            console.error('Failed to load all-other cards:', err);
            setBottomLoading(false);
        });
    }, []);

    // Initial load + refresh
    useEffect(() => {
        loadTopSection(topTimeframe);
    }, [topTimeframe, refreshKey, loadTopSection]);

    useEffect(() => {
        setBottomPage(0);
        loadBottomSection(bottomTimeframe, bottomSort, 0);
    }, [bottomTimeframe, bottomSort, refreshKey, loadBottomSection]);

    // Intersection Observer for bottom section pagination
    useEffect(() => {
        if (!sentinelRef.current) return;
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && !bottomLoading && bottomPage + 1 < bottomTotalPages) {
                const nextPage = bottomPage + 1;
                setBottomPage(nextPage);
                loadBottomSection(bottomTimeframe, bottomSort, nextPage);
            }
        }, { threshold: 0.1 });
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [bottomLoading, bottomPage, bottomTotalPages, bottomTimeframe, bottomSort, loadBottomSection]);

    // --- Pin toggle (optimistic + API-4) ---
    const handlePinToggle = useCallback((enterpriseId) => {
        if (pinInFlight) return;

        const allCards = [...topCards, ...bottomCards];
        const card = allCards.find(c => c.enterpriseId === enterpriseId);
        if (!card) return;

        const wasPinned = !!card.userPinned;
        setPinInFlight(enterpriseId);

        // Optimistic update — flip the pin flag immediately so the icon responds instantly
        const togglePin = cards => cards.map(c => c.enterpriseId === enterpriseId ? { ...c, userPinned: !wasPinned } : c);
        setTopCards(prev => togglePin(prev));
        setBottomCards(prev => togglePin(prev));

        const apiCall = wasPinned
            ? PinnedEnterprisesService.unpin(enterpriseId)
            : PinnedEnterprisesService.pin(enterpriseId);

        apiCall.then(() => {
            // Silent background sync — rearranges cards without showing spinners
            loadTopSection(topTimeframe, true);
            setBottomPage(0);
            loadBottomSection(bottomTimeframe, bottomSort, 0, true);
        }).catch(err => {
            console.error('Pin toggle error:', err);
            // Revert optimistic update on failure
            setTopCards(prev => togglePin(prev));
            setBottomCards(prev => togglePin(prev));
        }).finally(() => {
            setPinInFlight(null);
        });
    }, [topCards, bottomCards, pinInFlight, topTimeframe, bottomTimeframe, bottomSort, loadTopSection, loadBottomSection]);

    // --- Protocol popover lazy-load (API-2) ---
    const handleProtocolHover = useCallback((enterpriseId) => {
        if (protocolCacheRef.current[enterpriseId]) return; // already cached
        EnterprisesService.getProtocolLocations(enterpriseId).then(res => {
            setProtocolCache(prev => ({ ...prev, [enterpriseId]: res.protocolLocations }));
        }).catch(err => {
            console.error('Failed to load protocol locations:', err);
        });
    }, []);

    const handleTopCardClick    = useCallback((id) => onCardClick(id, topTimeframe),    [onCardClick, topTimeframe]);
    const handleBottomCardClick = useCallback((id) => onCardClick(id, bottomTimeframe), [onCardClick, bottomTimeframe]);

    // --- Sort top section client-side ---
    const sortedTopCards = sortCards(topCards, topSort);
    const pinnedCards    = sortedTopCards.filter(c => c.userPinned);
    const attentionCards = sortedTopCards.filter(c => !c.userPinned);

    // --- Filter bottom section client-side for alerts-disabled toggle ---
    let displayedBottomCards = bottomCards;
    if (alertsDisabledOnly) {
        displayedBottomCards = bottomCards.filter(c => c.locationsWithAlertsDisabled > 0);
    }

    return (
        <div className="enterprise-dashboard__status">

            {/* Top section — needs attention */}
            <div className="enterprise-dashboard__section">
                <div className="enterprise-dashboard__section-header">
                    <WarningIcon />
                    <h3 className="enterprise-dashboard__section-title">Enterprises Needing Attention</h3>
                    <SectionControls
                        timeframe={topTimeframe}
                        onTimeframeChange={setTopTimeframe}
                        sort={topSort}
                        onSortChange={setTopSort}
                    />
                </div>
                {topLoading ? (
                    <div className="enterprise-dashboard__section-loading">Loading...</div>
                ) : (
                    <>
                        <CardGrid
                            cards={[...pinnedCards, ...attentionCards]}
                            timeframe={topTimeframe}
                            onPinToggle={handlePinToggle}
                            onCardClick={handleTopCardClick}
                            protocolCache={protocolCache}
                            onProtocolHover={handleProtocolHover}
                        />
                        {sortedTopCards.length === 0 && (
                            <div className="enterprise-dashboard__empty">No enterprises need attention</div>
                        )}
                    </>
                )}
            </div>

            {/* Bottom section — all other enterprises */}
            <div className="enterprise-dashboard__section">
                <div className="enterprise-dashboard__section-header">
                    <OkIcon />
                    <h3 className="enterprise-dashboard__section-title">All Other Enterprises</h3>
                    <SectionControls
                        timeframe={bottomTimeframe}
                        onTimeframeChange={setBottomTimeframe}
                        sort={bottomSort}
                        onSortChange={setBottomSort}
                    />
                </div>
                <div className="enterprise-dashboard__alerts-filter">
                    <label>
                        <input
                            type="checkbox"
                            checked={alertsDisabledOnly}
                            onChange={e => setAlertsDisabledOnly(e.target.checked)}
                        />
                        Only show Enterprises with Locations that have Alerts disabled
                    </label>
                </div>
                {bottomLoading && bottomCards.length === 0 ? (
                    <div className="enterprise-dashboard__section-loading">Loading...</div>
                ) : (
                    <div className={bottomLoading && bottomPage === 0 ? 'enterprise-dashboard__stale' : ''}>
                        <CardGrid
                            cards={displayedBottomCards}
                            timeframe={bottomTimeframe}
                            onPinToggle={handlePinToggle}
                            onCardClick={handleBottomCardClick}
                            protocolCache={protocolCache}
                            onProtocolHover={handleProtocolHover}
                        />
                    </div>
                )}
                {/* Sentinel for Intersection Observer pagination */}
                <div ref={sentinelRef} className="enterprise-dashboard__sentinel" />
                {bottomLoading && bottomCards.length > 0 && bottomPage > 0 && (
                    <div className="enterprise-dashboard__section-loading">Loading more...</div>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Totals tab
// ---------------------------------------------------------------------------

const StatTile = ({ label, value, alert }) => (
    <div className="enterprise-totals__stat-tile">
        <span className={`enterprise-totals__stat-value${alert ? ' is-alert' : ''}`}>{value}</span>
        <span className="enterprise-totals__stat-label">{label}</span>
    </div>
);

const OfflineTile = ({ label, offline, total }) => {
    const online = total - offline;
    const offlinePct = total > 0 ? Math.round((offline / total) * 100) : 0;
    const onlinePct = total > 0 ? online / total : 1;
    const statusClass = onlinePct === 1 ? 'is-ok' : onlinePct >= 0.57 ? 'is-warn' : 'is-alert';
    return (
        <div className="enterprise-totals__offline-tile">
            <div className="enterprise-totals__offline-label">{label}</div>
            <div className={`enterprise-totals__offline-fraction ${statusClass}`}>
                {online}
                <span className="enterprise-totals__offline-denom">/{total}</span>
                <span className="enterprise-totals__offline-online-tag"> online</span>
            </div>
            <div className={`enterprise-totals__offline-pct ${statusClass}`}>{offlinePct}% offline</div>
            <div className="enterprise-totals__offline-bar-track">
                <div
                    className={`enterprise-totals__offline-bar-fill ${statusClass}`}
                    style={{ width: `${offlinePct}%` }}
                />
            </div>
        </div>
    );
};

const EnterpriseTotals = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats]     = useState(null);

    useEffect(() => {
        Promise.all([
            DashboardService.getAdminDashboardData(),
            DashboardService.getAdminNeedsAttentionDashboardData(),
        ]).then(([primaryRes, secondaryRes]) => {
            const { enterprises, adminDashboardEnterpriseSummaries } = primaryRes.data;

            let totalUsers = 0;
            let totalLocations = 0;
            const deviceTypeCountMap = {};

            Object.values(adminDashboardEnterpriseSummaries).forEach(entry => {
                totalUsers += entry.activeUserCount || 0;
                const locs = entry.adminDashboardLocations;
                totalLocations += Array.isArray(locs) ? locs.length : Object.keys(locs || {}).length;
                Object.entries(entry.deviceTypeCounts || {}).forEach(([type, count]) => {
                    deviceTypeCountMap[type] = (deviceTypeCountMap[type] || 0) + count;
                });
            });

            const totalDeviceCount = Object.values(deviceTypeCountMap).reduce((sum, n) => sum + n, 0);

            const topDeviceTypes = Object.entries(deviceTypeCountMap)
                .map(([type, count]) => ({ type, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);

            let gatewaysOffline = 0, gatewaysTotal = 0;
            let routerOffline = 0,   routerTotal = 0;
            let sensorsOffline = 0,  sensorsTotal = 0;
            let criticalAlertCount = 0;

            Object.values(secondaryRes.data).forEach(locations => {
                locations.forEach(loc => {
                    gatewaysOffline    += loc.gatewaysOffline || 0;
                    gatewaysTotal      += loc.gatewaysTotal || 0;
                    routerOffline      += loc.routerOffline || 0;
                    routerTotal        += loc.routerTotal || 0;
                    sensorsOffline     += loc.sensorsOffline || 0;
                    sensorsTotal       += loc.sensorsTotal || 0;
                    criticalAlertCount += loc.criticalAlertCount || 0;
                });
            });

            setStats({
                enterpriseCount: enterprises.length,
                totalUsers,
                totalLocations,
                topDeviceTypes,
                totalDeviceCount,
                gatewaysOffline, gatewaysTotal,
                routerOffline,   routerTotal,
                sensorsOffline,  sensorsTotal,
                criticalAlertCount,
            });
            setLoading(false);
        }).catch(err => {
            console.error('Failed to load totals:', err);
            setLoading(false);
        });
    }, []);

    if (loading) return <div className="enterprise-dashboard__section-loading">Loading...</div>;
    if (!stats)  return null;

    const totalDeviceCount = stats.totalDeviceCount || 1;

    return (
        <div className="enterprise-totals">
            <div className="enterprise-totals__section-label">System Overview</div>
            <div className="enterprise-totals__stat-grid">
                <StatTile label="Enterprises"     value={stats.enterpriseCount} />
                <StatTile label="Locations"       value={stats.totalLocations} />
                <StatTile label="Users"           value={stats.totalUsers} />
                <StatTile label="Critical Alerts" value={stats.criticalAlertCount} alert={stats.criticalAlertCount > 0} />
            </div>

            <div className="enterprise-totals__section-label">Device Health</div>
            <div className="enterprise-totals__offline-grid">
                <OfflineTile label="Gateways" offline={stats.gatewaysOffline} total={stats.gatewaysTotal} />
                <OfflineTile label="Sensors"  offline={stats.sensorsOffline}  total={stats.sensorsTotal} />
                <OfflineTile label="Routers"  offline={stats.routerOffline}   total={stats.routerTotal} />
            </div>

            {stats.topDeviceTypes.length > 0 && (
                <>
                    <div className="enterprise-totals__section-label">Top Device Types</div>
                    <div className="enterprise-totals__device-types">
                        {stats.topDeviceTypes.map(({ type, count }) => (
                            <div key={type} className="enterprise-totals__device-type-row">
                                <span className="enterprise-totals__device-type-name">{type}</span>
                                <div className="enterprise-totals__device-type-bar">
                                    <div
                                        className="enterprise-totals__device-type-fill"
                                        style={{ width: `${Math.round((count / totalDeviceCount) * 100)}%` }}
                                    />
                                </div>
                                <span className="enterprise-totals__device-type-count">{count}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main dashboard component
// ---------------------------------------------------------------------------

const EnterpriseDashboard = () => {
    const [enterprises, setEnterprises]     = useState([]);
    const [loading, setLoading]             = useState(true);
    const [refreshKey, setRefreshKey]       = useState(0);

    const [activeTab, setActiveTab]                       = useState('status');
    const [selectedEnterpriseId, setSelectedEnterpriseId] = useState(null);
    const [navigateTimeframe, setNavigateTimeframe]       = useState(null);

    // Load enterprise list for search dropdown + Enterprise Detail tab
    useEffect(() => {
        setLoading(true);
        EnterprisesService.getAll().then(res => {
            setEnterprises((res.enterprises || []).map(e => ({ id: e.id, name: e.name })));
            setLoading(false);
        }).catch(err => {
            console.error('Failed to load enterprise list:', err);
            setLoading(false);
        });
    }, []);

    const handleRefresh = useCallback(() => {
        setRefreshKey(k => k + 1);
    }, []);

    const navigateToDetail = useCallback((enterpriseId, timeframe) => {
        setSelectedEnterpriseId(enterpriseId);
        setNavigateTimeframe(timeframe !== undefined ? timeframe : null);
        setActiveTab('detail');
    }, []);

    if (loading) {
        return <div className="loader">Loading...</div>;
    }

    const enterpriseOptions = enterprises.map(e => ({ value: e.id, label: e.name }));

    const makeSearch = (extraClass) => (
        <div className={`enterprise-dashboard__search-wrapper ${extraClass}`}>
            <i className="enterprise-dashboard__search-icon" />
            <Select
                className="enterprise-dashboard__search"
                classNamePrefix="enterprise-dashboard__search"
                options={enterpriseOptions}
                placeholder="Jump to Enterprise Detail"
                isClearable
                styles={{ valueContainer: base => ({ ...base, paddingLeft: 30 }) }}
                onChange={opt => opt && navigateToDetail(opt.value)}
            />
        </div>
    );

    const showHeaderSearch = activeTab === 'status';

    return (
        <div className="animated fadeIn enterprise-dashboard">
            <div className="row">
                <PageTitle title="Admin Dashboard" refreshFunction={handleRefresh} rightContent={showHeaderSearch ? makeSearch('enterprise-dashboard__search-wrapper--header') : null} />
            </div>
            <div className="row">{makeSearch('enterprise-dashboard__search-wrapper--inline')}</div>
            <div className="row">
                <div className="op-content-box">
                    <Tabs activeKey={activeTab} onSelect={setActiveTab} id="enterprise-dashboard-tabs">
                        <Tab eventKey="status" title="Status">
                            <EnterpriseDashboardStatus
                                onCardClick={navigateToDetail}
                                refreshKey={refreshKey}
                            />
                        </Tab>
                        <Tab eventKey="detail" title="Enterprise Detail">
                            <EnterpriseDetail
                                enterprises={enterprises}
                                enterpriseId={selectedEnterpriseId}
                                onEnterpriseSelect={setSelectedEnterpriseId}
                                initialTimeframe={navigateTimeframe}
                            />
                        </Tab>
                        <Tab eventKey="totals" title="Totals">
                            <EnterpriseTotals />
                        </Tab>
                    </Tabs>
                </div>
            </div>
        </div>
    );
};

export default EnterpriseDashboard;
